import { useTranslation } from "react-i18next";

import useLocations from "../../data/useLocations";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useSiteScope from "../../hooks/useSiteScope";
import { IVehicle } from "../../models/Vehicle";
import ComboBoxInput from "../shared/Inputs/ComboBoxInput";
import SiteScopeSelect from "../sites/SiteScopeSelect";

export default function VehicleLocationDropdown({ vehicle, classes = "", size = "md" }: { vehicle: IVehicle | undefined, classes?: string, size?: "sm" | "md" | "lg" }) {
  const { t } = useTranslation("vehicles");
  const { locations } = useLocations();
  const { patchVehicle } = useVehiclesMutations();
  const { saveHandlers } = useDefaultCRUDHandlers(t("Vehicle Location"));

  const allLocations = locations.data?.data ?? [];
  const currentSiteId = allLocations.find((location) => location.id === vehicle?.locationId)?.siteId;
  const { siteId, setSiteId, hasSites, sites, scope } = useSiteScope(currentSiteId);

  const locationOptions = [{ id: "", name: t("No Location") }, ...scope(allLocations, vehicle?.locationId)];

  const handleLocationUpdate = (locationId: string) => {
    if (!vehicle) return;

    patchVehicle.mutate(
      { vehicleId: vehicle.id, patch: { locationId: locationId || null, updateLocationId: true } },
      {
        onSuccess: () => saveHandlers.onSuccess(),
        onError: () => saveHandlers.onError(),
      }
    );
  }

  const handleSetValue = (_registerName: string, value: string) => {
    if (!vehicle) return;

    const nextLocationId = value ? value : null;
    if (vehicle.locationId === nextLocationId) return;

    handleLocationUpdate(value);
  };

  if (locations.isLoading || !vehicle) {
    return <></>
  }

  return (
    <div className="w-full h-full flex items-center gap-x-1" onClick={(e) => e.stopPropagation()}>
      {hasSites && (
        <SiteScopeSelect value={siteId} onChange={setSiteId} sites={sites} size={size} className="mt-1" />
      )}
      <ComboBoxInput
        key={`vehicle-location-${vehicle.id}-${vehicle.locationId ?? "none"}`}
        options={locationOptions}
        searchField={"name"}
        valueField={"id"}
        registerName={"locationId"}
        setValue={handleSetValue}
        register={null}
        initialValue={vehicle.locationId ?? ""}
        placeholder={t("Search for a Yard Location")}
        classes={classes}
        containerClasses="w-full"
        size={size}
      />
    </div>
  )
}
